import { FC, ReactNode } from "react";
import { Box, makeStyles } from "@material-ui/core";

import {
  BLOG_MUSIC_ROYALTIES_URL,
  EMPTY_VALUE_PLACEHOLDER,
} from "constants/index";
import { IncludedRights } from "services/includedRights/types";
import { RoyaltyPayorKind } from "services/royaltyPayor/types";

const useStyles = makeStyles(theme => ({
  includedRightsTable: {
    "& h2": {
      fontSize: "1.625rem",
      fontWeight: theme.typography.fontWeightBold,
      marginTop: theme.spacing(0.375),
    },
    "& table": {
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[3],
      width: "100%",
      "& th": {
        padding: theme.spacing(1),
        color: theme.palette.primary.main,
        fontSize: 14,
        border: "2px solid #e1e1e3",
        fontWeight: 400,
      },
      "& tr": {
        display: "flex",
        flexFlow: "row wrap",
      },
      "& td": {
        padding: theme.spacing(1),
        fontSize: 14,
        border: "2px solid #e1e1e3",
        textAlign: "center",
      },
      "& thead > tr > th:first-of-type": {
        backgroundColor: theme.custom.palette.gray.main,
        width: "100%",
        borderBottom: 0,
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius,
      },
      "& thead > tr > th:nth-of-type(2), & thead > tr > th:nth-of-type(3)": {
        width: "50%",
      },
      "& thead > tr > th:nth-of-type(3)": {
        borderLeft: 0,
      },
      "& tbody > tr > td:first-of-type": {
        backgroundColor: theme.custom.palette.gray.main,
        width: "100%",
        borderTop: 0,
        borderBottom: 0,
      },
      "& tbody > tr:last-of-type > td:nth-last-of-type(2)": {
        borderBottomLeftRadius: theme.shape.borderRadius,
      },
      "& tbody > tr:last-of-type > td:last-of-type": {
        borderBottomRightRadius: theme.shape.borderRadius,
      },
      "& tbody > tr > td": {
        width: "50%",
      },
      "& tbody > tr > td:nth-of-type(3)": {
        borderLeft: 0,
      },
      "& .cell-bold": {
        fontWeight: theme.typography.fontWeightMedium,
      },
      "& .cell-light": {
        color: "#94989d",
      },
      "& .cell-multi": {
        padding: 0,
        "& .cell-bold": {
          fontWeight: theme.typography.fontWeightMedium,
          color: theme.palette.primary.main,
        },
        "& div": {
          marginTop: 0,
          padding: theme.spacing(1),
          color: "#94989d",
        },
        "& div:not(:first-of-type)": {
          borderTop: "1px solid #e1e1e3",
        },
      },
    },
    "& div": {
      textAlign: "center",
      marginTop: theme.spacing(2.5),
      "& a": {
        fontSize: "1rem",
        color: theme.custom.palette.blue.main,
      },
      "& a:hover": {
        textDecoration: "underline",
      },
    },
    [theme.breakpoints.down("xs")]: {
      "& table": {
        "& th": {
          fontSize: 13,
        },
        "& td": {
          padding: theme.spacing(1, 0.625),
          fontSize: 13,
        },
        "& .cell-multi div": {
          padding: theme.spacing(1, 0.625),
        },
      },
    },
  },
}));

const getCellEmphasisClass = (emphasis: "bold" | "light" | undefined) => {
  return emphasis === "bold"
    ? "cell-bold"
    : emphasis === "light"
    ? "cell-light"
    : "";
};
interface CellProps {
  isBold?: boolean;
  isLight?: boolean;
  component?: string;
  children?: ReactNode;
}

const Cell = ({ isBold, isLight, children, component }: CellProps) => {
  const Component: any = component || "td";
  return (
    <Component
      className={getCellEmphasisClass(
        isBold ? "bold" : isLight ? "light" : undefined,
      )}
    >
      {children}
    </Component>
  );
};

// Omit children from props since we're providing them
type HelperCellProps = Omit<CellProps, "children">;

const NoCell = (props: HelperCellProps) => <Cell {...props}>No</Cell>;
const YesCell = (props: HelperCellProps) => <Cell {...props}>Yes</Cell>;
const EmptyCell = (props: HelperCellProps) => <Cell {...props}>&#8212;</Cell>;

interface RoyaltiesTableProps {
  royaltyPayorNames: string[];
  royaltyPayorKind: RoyaltyPayorKind;
  includedRights: IncludedRights;
}

const RoyaltiesTable: FC<RoyaltiesTableProps> = ({
  royaltyPayorNames,
  royaltyPayorKind,
  includedRights,
}) => {
  const classes = useStyles();
  const copyrightType = ["digital_distributor", "label"].includes(
    royaltyPayorKind,
  )
    ? "sound-recording"
    : "musical-composition";
  return (
    <Box className={classes.includedRightsTable}>
      {includedRights?.sound_recording_rights ||
      includedRights?.musical_composition_rights ? (
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th
                className={getCellEmphasisClass(
                  includedRights.musical_composition_rights.length > 0
                    ? "bold"
                    : "light",
                )}
              >
                Musical Composition
              </th>
              <th
                className={getCellEmphasisClass(
                  includedRights.sound_recording_rights.length > 0
                    ? "bold"
                    : "light",
                )}
              >
                Sound Recording
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Cell>Copyrights Included?</Cell>
              {includedRights.musical_composition_copyright_included ? (
                <YesCell isBold />
              ) : (
                <NoCell
                  isBold={
                    includedRights.musical_composition_rights.length > 0 ||
                    includedRights.musical_composition_copyright_included
                  }
                  isLight={
                    includedRights.musical_composition_rights.length === 0 &&
                    !includedRights.musical_composition_copyright_included
                  }
                />
              )}
              {includedRights.sound_recording_copyright_included ? (
                <YesCell isBold />
              ) : (
                <NoCell
                  isBold={
                    includedRights.sound_recording_rights.length > 0 ||
                    includedRights.sound_recording_copyright_included
                  }
                  isLight={
                    includedRights.sound_recording_rights.length === 0 &&
                    !includedRights.sound_recording_copyright_included
                  }
                />
              )}
            </tr>
            <tr>
              <td>Royalty Types</td>
              <td className="cell-multi">
                <Cell
                  isBold={
                    includedRights.musical_composition_rights.indexOf(
                      "mechanical",
                    ) > -1
                  }
                  component="div"
                >
                  Mechanical
                </Cell>
                <Cell
                  isBold={
                    includedRights.musical_composition_rights.indexOf(
                      "public_performance",
                    ) > -1
                  }
                  component="div"
                >
                  Public Performance
                </Cell>
                <Cell
                  isBold={
                    includedRights.musical_composition_rights.indexOf("sync") >
                    -1
                  }
                  component="div"
                >
                  Sync
                </Cell>
              </td>
              <td className="cell-multi">
                <Cell
                  isBold={
                    includedRights.sound_recording_rights.indexOf("sales") > -1
                  }
                  component="div"
                >
                  Sales
                </Cell>
                <Cell
                  isBold={
                    includedRights.sound_recording_rights.indexOf("streaming") >
                    -1
                  }
                  component="div"
                >
                  Streaming
                  <br />
                </Cell>
                <Cell
                  isBold={
                    includedRights.sound_recording_rights.indexOf("sync") > -1
                  }
                  component="div"
                >
                  Sync
                </Cell>
              </td>
            </tr>
            <tr>
              <Cell>Sources</Cell>
              <Cell
                isBold={
                  (includedRights.musical_composition_rights.length > 0 ||
                    includedRights.musical_composition_copyright_included) &&
                  includedRights.musical_composition_sources.length > 0
                }
                isLight={
                  !includedRights.musical_composition_copyright_included &&
                  includedRights.musical_composition_rights.length === 0
                }
              >
                {includedRights.musical_composition_sources ||
                  EMPTY_VALUE_PLACEHOLDER}
              </Cell>
              <Cell
                isBold={
                  (includedRights.sound_recording_rights.length > 0 ||
                    includedRights.sound_recording_copyright_included) &&
                  includedRights.sound_recording_sources.length > 0
                }
                isLight={
                  !includedRights.sound_recording_copyright_included &&
                  includedRights.sound_recording_rights.length === 0
                }
              >
                {includedRights.sound_recording_sources ||
                  EMPTY_VALUE_PLACEHOLDER}
              </Cell>
            </tr>
            <tr>
              <Cell>Distributors</Cell>
              {includedRights.musical_composition_distributors.length > 0 ? (
                <Cell isBold>
                  {includedRights.musical_composition_distributors}
                </Cell>
              ) : (
                <EmptyCell isLight />
              )}

              {includedRights.sound_recording_distributors.length > 0 ? (
                <Cell isBold>
                  {includedRights.sound_recording_distributors}
                </Cell>
              ) : (
                <EmptyCell isLight />
              )}
            </tr>
          </tbody>
        </table>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th
                className={getCellEmphasisClass(
                  copyrightType === "musical-composition" ? "bold" : "light",
                )}
              >
                Musical Composition
              </th>
              <th
                className={getCellEmphasisClass(
                  copyrightType === "sound-recording" ? "bold" : "light",
                )}
              >
                Sound Recording
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Cell>Copyrights Included?</Cell>
              {copyrightType === "musical-composition"
                ? [
                    <NoCell key="no-musical-composition" isBold />,
                    <EmptyCell key="empty-musical-composition" isLight />,
                  ]
                : [
                    <EmptyCell key="empty-musical-composition" isLight />,
                    <NoCell key="no-musical-composition" isBold />,
                  ]}
            </tr>
            <tr>
              <td>Royalty Types</td>
              <td className="cell-multi">
                <Cell
                  isBold={
                    copyrightType === "musical-composition" &&
                    royaltyPayorKind === "publisher"
                  }
                  component="div"
                >
                  Mechanical
                </Cell>
                <Cell
                  isBold={copyrightType === "musical-composition"}
                  component="div"
                >
                  Public Performance
                </Cell>
                <Cell
                  isBold={
                    copyrightType === "musical-composition" &&
                    royaltyPayorKind === "publisher"
                  }
                  component="div"
                >
                  Sync
                </Cell>
              </td>
              <td className="cell-multi">
                <Cell
                  isBold={copyrightType === "sound-recording"}
                  component="div"
                >
                  Sales
                </Cell>
                <Cell
                  isBold={copyrightType === "sound-recording"}
                  component="div"
                >
                  Streaming
                  <br />
                </Cell>
                <Cell isBold={royaltyPayorKind === "label"} component="div">
                  Sync
                </Cell>
              </td>
            </tr>
            <tr>
              <Cell>Sources</Cell>
              <Cell
                isBold={copyrightType === "musical-composition"}
                isLight={copyrightType !== "musical-composition"}
              >
                {royaltyPayorKind === "publisher"
                  ? "Internet Streaming, AM/FM & Satellite Radio, Digital Downloads, CD Sales, TV/Film/Commercial Placements & Performances, etc."
                  : "Internet Streaming, AM/FM & Satellite Radio, TV/Film/Commercial Performances, etc."}
              </Cell>
              <Cell
                isBold={copyrightType === "sound-recording"}
                isLight={copyrightType !== "sound-recording"}
              >
                {royaltyPayorKind === "digital_distributor"
                  ? "Internet Streaming, Digital Downloads, etc."
                  : royaltyPayorKind === "label"
                  ? "Internet Streaming, Satellite Radio, Digital Downloads, CD Sales, TV/Film/Commercial Placements, Samples, etc."
                  : "Internet Streaming, Satellite Radio, Digital Downloads, CD Sales, TV/Film/Commercial Placements & Performances, Samples, etc."}
              </Cell>
            </tr>
            <tr>
              <Cell>Distributors</Cell>
              {copyrightType === "musical-composition"
                ? [
                    <Cell isBold key="musical-composition-payor">
                      {royaltyPayorNames.join(", ")}
                    </Cell>,
                    <EmptyCell isLight key="empty-musical-composition-payor" />,
                  ]
                : [
                    <EmptyCell isLight key="empty-musical-composition-payor" />,
                    <Cell isBold key="musical-composition-payor">
                      {royaltyPayorNames.join(", ")}
                    </Cell>,
                  ]}
            </tr>
          </tbody>
        </table>
      )}

      <div>
        <a
          href={BLOG_MUSIC_ROYALTIES_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more about different rights and royalties&nbsp;&gt;
        </a>
      </div>
    </Box>
  );
};

export default RoyaltiesTable;
